import {
  autoUpdate,
  flip,
  limitShift,
  offset,
  shift,
  size,
  useFloating
} from "@floating-ui/react-dom";
import { Box, Button, Heading, Text, TextButton } from "@wix/design-system";
import { RegenerateSparkle, SparklesFilled, StopFilled } from "@wix/wix-ui-icons-common";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { EditorContext, RicosContext, ZIndexContext } from "./../../ricos-context/index.js";
import { useModalService } from "./../../ricos-modals/modal-service-provider.js";
import { isOnlyNodeSelected } from "./../../_shared/is-only-node-selected.js";
import { logInvariant } from "./../../_shared/log-invariant.js";
import { getPluginFocusClassNames } from "./../../_shared/node-styles.js";
import { PromptInputArea } from "./../../_shared/prompt-input-area/prompt-input-area.js";
import { useRequestService } from "./../../_shared/request-service-provider/request-service-provider.js";
import { useNodeAttrs } from "./../../_shared/use-node-attrs.js";
import { uuid } from "./../../wix-rich-content-common/index.js";
import { useToolbarBoundary } from "./../../toolbars-v3/toolbar-boundary.js";
import { ClickOutside } from "./../../ui-components/index.js";
import { useHtmlPluginContext } from "../context.js";
import classes from "./ai-widget-scss.js";
import { WIDGET_TYPE_OPTIONS } from "./common.js";
import { doPrompt } from "./do-prompt.js";
import { ErrorIcon } from "./error-icon.js";
import { Loader } from "./loader.js";
import { openFloatingPromptInput } from "./open-floating-prompt-input.js";
import { FIX_BUTTON_PRESET, PresetBar } from "./preset-bar/preset-bar.js";
const DATA_HOOKS = {
  EDITOR: "fullRicosEditor",
  AI_WIDGET_CONTAINER: "ai-widget-container",
  AI_WIDGET_FLOATING_PROMPT: "ai-widget-floating-prompt",
  AI_WIDGET_HTML_VIEWER: "ai-widget-html-viewer",
  PROMPT_INPUT_AREA: "prompt-input-area"
};
const FIX_WIDGET_PROMPT = "Widget is broken. Analyze the HTML, find issues and fix them.";
const AiWidget = (props) => {
  const { ricosNode, children } = props;
  const { jsdomMode, t, lastInteraction, portal } = React.useContext(RicosContext);
  const zIndexService = React.useContext(ZIndexContext);
  const { patchPmNodeAttrs, pmNodeAttrs } = useNodeAttrs(ricosNode?.id, "HTML");
  const { makeRequest } = useRequestService();
  const {
    adapter: { tiptapEditor: editor },
    getPublicCommands
  } = React.useContext(EditorContext);
  const modals = useModalService();
  const { state, setState } = useHtmlPluginContext();
  const promptInputAreaRef = React.useRef(null);
  const aiWidgetContainerRef = useRef(null);
  const boundary = useToolbarBoundary();
  const [hideFromClickOutside, setHideFromClickOutside] = useState(false);
  const isWidgetSelected = pmNodeAttrs?.id ? isOnlyNodeSelected(editor.state, pmNodeAttrs.id) : false;
  const {
    x,
    y,
    refs: { setReference, setFloating },
    strategy,
    middlewareData,
    placement
  } = useFloating({
    placement: "top",
    middleware: [
      offset({ mainAxis: 2 }),
      flip({
        boundary: boundary || void 0,
        fallbackPlacements: ["bottom"]
      }),
      shift({
        crossAxis: true,
        mainAxis: false,
        limiter: limitShift()
      }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`
          });
        }
      })
    ],
    whileElementsMounted: (referenceEl, floatingEl, update) => autoUpdate(referenceEl, floatingEl, update, {
      animationFrame: true
    })
  });
  const document = getPublicCommands().getContent();
  if (!pmNodeAttrs) {
    logInvariant(new Error("AI Widget: pmNodeAttrs is undefined"));
    return null;
  }
  const isFloatingPromptVisible = useMemo(
    () => shouldShowFloatingPrompt({
      isWidgetSelected,
      jsdomMode,
      middlewareData,
      x,
      y,
      hideFromClickOutside
    }),
    [isWidgetSelected, jsdomMode, middlewareData, x, y, hideFromClickOutside]
  );
  useEffect(() => {
    if (isWidgetSelected) {
      setHideFromClickOutside(false);
    }
  }, [isWidgetSelected]);
  const handleWidgetClick = useCallback(() => {
    if (hideFromClickOutside) {
      setHideFromClickOutside(false);
    }
  }, [hideFromClickOutside]);
  useEffect(() => {
    if (lastInteraction === "mouse" && isFloatingPromptVisible && promptInputAreaRef.current) {
      promptInputAreaRef.current?.focus();
    }
  }, [isFloatingPromptVisible, lastInteraction]);
  const handleSubmit = React.useCallback(
    async ({ text, shouldFixWidget }) => {
      patchPmNodeAttrs({ loading: true, error: null });
      const currentRequestId = uuid();
      const nodeId = pmNodeAttrs.id;
      setState((prev) => ({
        ...prev,
        [nodeId]: {
          ...prev[nodeId],
          status: "LOADING",
          requestId: currentRequestId,
          promptText: shouldFixWidget ? prev[nodeId]?.promptText : text
        }
      }));
      const response = await doPrompt({
        prompt: shouldFixWidget ? FIX_WIDGET_PROMPT : text,
        document,
        selection: [ricosNode],
        makeRequest,
        widgetType: WIDGET_TYPE_OPTIONS.find(
          (option) => option.id === state[nodeId]?.selectedWidgetTypeId
        )?.label
      });
      setState((prev) => {
        const nodeState = prev[nodeId];
        if (nodeState?.status === "LOADING" && nodeState.requestId === currentRequestId && response.type === "SUCCESS") {
          patchPmNodeAttrs({
            loading: false,
            html: response.html,
            containerData: response.containerData
          });
          return {
            ...prev,
            [nodeId]: {
              ...prev[nodeId],
              status: "SUCCESS",
              html: response.html,
              promptText: ""
            }
          };
        }
        if (nodeState?.status === "LOADING" && nodeState.requestId === currentRequestId && response.type === "ERROR") {
          patchPmNodeAttrs({ loading: false, error: response.error });
          return {
            ...prev,
            [nodeId]: {
              ...prev[nodeId],
              status: "ERROR",
              error: response.error,
              promptText: nodeState.promptText
            }
          };
        }
        return prev;
      });
    },
    [patchPmNodeAttrs, pmNodeAttrs.html, pmNodeAttrs.id, makeRequest, setState]
  );
  const handleCancel = React.useCallback(() => {
    const nodeId = pmNodeAttrs.id;
    setState((prev) => ({
      ...prev,
      [nodeId]: { ...prev[nodeId], status: "IDLE" }
    }));
    patchPmNodeAttrs({ loading: false });
  }, [patchPmNodeAttrs, pmNodeAttrs.id, setState]);
  const isClickInsideAllowedArea = useCallback(
    (target) => {
      const isInEditor = target.closest(`[data-hook~="${DATA_HOOKS.EDITOR}"]`) !== null;
      const isInPortal = portal?.contains(target) ?? false;
      const isInAiWidget = aiWidgetContainerRef.current?.contains(target) ?? false;
      return isInEditor || isInPortal || isInAiWidget;
    },
    [portal]
  );
  const handleClickOutside = useCallback(
    (e) => {
      const target = e.target;
      if (!isClickInsideAllowedArea(target)) {
        setHideFromClickOutside(true);
      }
    },
    [isClickInsideAllowedArea]
  );
  if (!pmNodeAttrs.html) {
    if (pmNodeAttrs.error) {
      return /* @__PURE__ */ React.createElement(
        ErrorDisplay,
        {
          onRetry: () => {
            handleSubmit({ text: state[pmNodeAttrs.id]?.promptText ?? "" });
          },
          onNewPrompt: () => {
            setState((prev) => ({
              ...prev,
              [pmNodeAttrs.id]: { ...prev[pmNodeAttrs.id], status: "IDLE", promptText: "" }
            }));
            getPublicCommands().deleteBlock(pmNodeAttrs.id);
            openFloatingPromptInput({
              nodeId: pmNodeAttrs.id,
              editor,
              modals
            });
          },
          t
        }
      );
    }
    if (pmNodeAttrs.loading) {
      return /* @__PURE__ */ React.createElement(
        LoadingDisplay,
        {
          onStop: () => {
            const nodeId = pmNodeAttrs.id;
            setState((prev) => ({
              ...prev,
              [nodeId]: { ...prev[nodeId], status: "IDLE" }
            }));
            getPublicCommands().deleteBlock(nodeId);
            openFloatingPromptInput({
              nodeId,
              editor,
              modals
            });
          },
          t
        }
      );
    }
    logInvariant(new Error("AI Widget: pmNodeAttrs.html is undefined"));
    return null;
  }
  const floatingPrompt = portal && /* @__PURE__ */ React.createElement(ClickOutside, { onClickOutside: handleClickOutside }, /* @__PURE__ */ React.createElement(
    "div",
    {
      ref: setFloating,
      "data-hook": DATA_HOOKS.AI_WIDGET_FLOATING_PROMPT,
      "data-node-id": pmNodeAttrs.id,
      style: {
        position: strategy,
        top: y ?? "",
        left: x ?? "",
        zIndex: zIndexService.getZIndex("TOOLBAR"),
        display: isFloatingPromptVisible ? "block" : "none"
      }
    },
    /* @__PURE__ */ React.createElement(
      PromptInputArea,
      {
        placeholder: t("HtmlPlugin_AiWidget_Input_Placeholder_Text"),
        ref: promptInputAreaRef,
        maxLength: 1e4,
        zIndex: 1e3,
        forceMultiline: true,
        dataHook: DATA_HOOKS.PROMPT_INPUT_AREA,
        readonly: Boolean(pmNodeAttrs.loading),
        busy: Boolean(pmNodeAttrs.loading),
        busyValue: state[pmNodeAttrs.id]?.promptText ?? "",
        value: state[pmNodeAttrs.id]?.promptText ?? "",
        onChange: (text) => {
          setState((prev) => ({
            ...prev,
            [pmNodeAttrs.id]: { ...prev[pmNodeAttrs.id], status: "IDLE", promptText: text }
          }));
        },
        onSubmit: ({ text }) => {
          handleSubmit({ text });
        },
        onCancel: handleCancel,
        minHeight: "48px",
        style: middlewareData.shift?.y ? {
          borderRadius: 0,
          borderBottom: "1px solid rgba(0, 6, 36, 0.1)",
          boxShadow: "0 0 8px 0 rgba(0, 6, 36, 0.1), 0 6px 6px 0 rgba(0, 6, 36, 0.05)"
        } : placement === "top" ? {
          borderBottomRightRadius: 0,
          borderBottomLeftRadius: 0,
          borderBottom: "none"
        } : {
          borderTopRightRadius: 0,
          borderTopLeftRadius: 0,
          borderTop: "none"
        },
        className: classes.promptInputArea,
        presetBarComponent: /* @__PURE__ */ React.createElement(
          PresetBar,
          {
            presets: PRESETS,
            onPresetSelect: (preset) => {
              if (pmNodeAttrs.loading) {
                return;
              }
              if (preset.id === FIX_BUTTON_PRESET.id) {
                handleSubmit({
                  text: FIX_WIDGET_PROMPT,
                  shouldFixWidget: true
                });
                return;
              }
              setState((prev) => ({
                ...prev,
                [pmNodeAttrs.id]: prev[pmNodeAttrs.id]?.status === "LOADING" ? prev[pmNodeAttrs.id] : {
                  ...prev[pmNodeAttrs.id],
                  status: "IDLE",
                  promptText: preset.prompt ?? ""
                }
              }));
            },
            showFixButton: true
          }
        )
      }
    )
  ));
  const shouldShowWrapper = isWidgetSelected && !hideFromClickOutside;
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      ref: aiWidgetContainerRef,
      className: shouldShowWrapper ? classes.wrapper : void 0,
      style: {
        width: "100%",
        zIndex: isFloatingPromptVisible ? zIndexService.getZIndex("TOOLBAR") - 1 : 1
      },
      "data-hook": DATA_HOOKS.AI_WIDGET_CONTAINER,
      onPointerDown: handleWidgetClick
    },
    portal && ReactDOM.createPortal(floatingPrompt, portal),
    /* @__PURE__ */ React.createElement(
      "div",
      {
        "data-hook": DATA_HOOKS.AI_WIDGET_HTML_VIEWER,
        "data-node-id": pmNodeAttrs.id,
        ref: setReference,
        className: getPluginFocusClassNames(isWidgetSelected)
      },
      pmNodeAttrs.loading ? /* @__PURE__ */ React.createElement(LoadingDisplay, { onStop: handleCancel, isIterating: true, t }) : pmNodeAttrs.error ? /* @__PURE__ */ React.createElement(
        ErrorDisplay,
        {
          onRetry: () => {
            handleSubmit({ text: state[pmNodeAttrs.id]?.promptText ?? "" });
          },
          t
        }
      ) : /* @__PURE__ */ React.createElement(React.Fragment, null, children)
    )
  );
};
const ErrorDisplay = ({ onRetry, onNewPrompt, t }) => {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, { padding: "48px", direction: "vertical", align: "center", height: "100%", width: "100%" }, /* @__PURE__ */ React.createElement(ErrorIcon, null), /* @__PURE__ */ React.createElement(
    Box,
    {
      direction: "vertical",
      width: "100%",
      align: "center",
      gap: "4px",
      marginTop: "30px",
      marginBottom: "24px"
    },
    /* @__PURE__ */ React.createElement(Heading, { size: "large" }, t("HtmlPlugin_AiWidget_Error_Message_Title")),
    /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "thin" }, t("HtmlPlugin_AiWidget_Error_Message_Subtitle"))
  ), /* @__PURE__ */ React.createElement(Box, { gap: "24px", align: "center", verticalAlign: "middle" }, onNewPrompt && /* @__PURE__ */ React.createElement(TextButton, { prefixIcon: /* @__PURE__ */ React.createElement(SparklesFilled, null), onClick: onNewPrompt }, t("HtmlPlugin_AiWidget_Error_NewPromptButton")), /* @__PURE__ */ React.createElement(
    Button,
    {
      priority: "secondary",
      skin: "ai",
      prefixIcon: /* @__PURE__ */ React.createElement(RegenerateSparkle, null),
      onClick: onRetry
    },
    t("HtmlPlugin_AiWidget_Error_RetryButton")
  ))));
};
const LoadingDisplay = ({ onStop, isIterating, t }) => {
  return /* @__PURE__ */ React.createElement(Box, { padding: "48px", direction: "vertical", align: "center", height: "100%", width: "100%" }, /* @__PURE__ */ React.createElement(Loader, null), /* @__PURE__ */ React.createElement(
    Box,
    {
      direction: "vertical",
      width: "100%",
      align: "center",
      gap: "4px",
      marginTop: "30px",
      marginBottom: "24px"
    },
    /* @__PURE__ */ React.createElement(Heading, { size: "large" }, isIterating ? t("HtmlPlugin_AiWidget_Loading_Iterating_Title") : t("HtmlPlugin_AiWidget_Loading_Building_Title")),
    /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "thin" }, isIterating ? t("HtmlPlugin_AiWidget_Loading_Iterating_Subtitle") : t("HtmlPlugin_AiWidget_Loading_Building_Subtitle"))
  ), !isIterating && /* @__PURE__ */ React.createElement(Button, { priority: "secondary", prefixIcon: /* @__PURE__ */ React.createElement(StopFilled, null), onClick: onStop }, t("HtmlPlugin_AiWidget_StopButton")));
};
const shouldShowFloatingPrompt = ({
  isWidgetSelected,
  jsdomMode,
  middlewareData,
  x,
  y,
  hideFromClickOutside
}) => {
  if (!isWidgetSelected) {
    return false;
  }
  if (jsdomMode) {
    return true;
  }
  const isFloatingPositionInvalid = middlewareData.hide?.escaped || middlewareData.hide?.referenceHidden || x === null && y === null;
  if (isFloatingPositionInvalid) {
    return false;
  }
  if (hideFromClickOutside) {
    return false;
  }
  return true;
};
const PRESETS = [
  {
    id: "preset-1",
    label: "More professional",
    prompt: "Refine tone, layout, and typography for a cleaner, more polished look."
  },
  {
    id: "preset-2",
    label: "More playful",
    prompt: "Introduce brighter colors, rounded shapes, or fun micro-interactions."
  },
  {
    id: "preset-3",
    label: "Hover effects",
    prompt: "Suggest subtle transitions or animations on hover."
  },
  {
    id: "preset-4",
    label: "Improve readability",
    prompt: "Enhance text contrast, spacing, and font legibility."
  },
  {
    id: "preset-5",
    label: "Prettier",
    prompt: "Improve overall visual appeal with a more polished design."
  }
];
export {
  AiWidget,
  PRESETS
};
//# sourceMappingURL=ai-widget.js.map
